import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import * as Sentry from '@sentry/browser';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { computed } from 'mobx';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Divider } from '@mui/material';
import ReactHtmlParser from 'react-html-parser';

import useSessionStore from '../../../../../../../hooks/useSessionStore';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import Plan from '../../../../../../../Model/Plan';
import { getCurrentPeriodEnd } from '../../../../../../../Model/Subscription';
import { StripeSubscriptionState } from '../../../../../../../pages/Onboarding/Subscription/SubscriptionMetadata';
import { DateFormat } from '../../../../../../../utils/date';
import { getUserStatusWithDate } from '../../../../../../utils/userStatus';
import { getUserSubscriptionPrice } from '../../../../../../utils/userContract';
import {
  canActivateOnContract,
  isLiveTarget,
  listedTargets,
  oneTargetPerProduct,
  productLabel,
} from '../../../../../../utils/billingTarget';
import useToast from '../../../../../../hooks/useToast';
import DialogRoundedModal from '../../../../../../../components/DialogRoundedModal';
import Accordion from '../../../../../../../components/Accordion';
import { SaveButton, CancelButton } from '../../../../../../../components/Button/ActionButtons';
import ContractChooserModal, { useContractChoice } from '../../../../../ContractChooserModal';
import InfoItem from '../../../InfoItem';
import colors from '../../../../../../../styles/colors';
import {
  InfoContainer,
  InfoWrapper,
  StyledLoadingPage,
  StyledSubTitle,
  ActivateUserModalContent,
  StyledOnCallActions,
  StyledTitle,
  StyledAccordion,
  StyledArrowIcon,
} from '../../styles';
import { accordionId } from '../../utils';

import texts from './texts.json';

const EMPTY_CELL = '-';

const SubscriptionDetailsSection = ({
  userDoc,
  billingTargets,
  handleExpand,
  expanded,
  isQAP,
}) => {
  const { id: userId } = userDoc;

  const {
    isOnCallUser,
    userId: loggedInUserId,
  } = useSessionStore();

  const { showToast } = useToast();

  const [planDocsById, setPlanDocsById] = useState({});
  const [activationTarget, setActivationTarget] = useState(null);
  const [isReady, setIsReady] = useState(false);

  const isComponentMountedRef = useComponentMounted();
  const {
    pendingCandidates,
    request: requestContractChoice,
    confirm: confirmContractChoice,
    dismiss: dismissContractChoice,
  } = useContractChoice();

  const planIds = useMemo(() => [...new Set(
    billingTargets.map(({ contractEntry }) => contractEntry.planId).filter(Boolean),
  )], [billingTargets]);

  useEffect(() => {
    const init = async () => {
      const missing = planIds.filter((planId) => !planDocsById[planId]);
      const loaded = await Promise.all(missing.map(async (planId) => [planId, await Plan.getPlanById(planId)]));
      if (isComponentMountedRef.current) {
        setPlanDocsById((previous) => ({
          ...previous,
          ...Object.fromEntries(loaded.filter(([, planDoc]) => !!planDoc)),
        }));
        setIsReady(true);
      }
    };
    init();
    // `planDocsById` is read to skip what is already loaded, not to trigger a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planIds, isComponentMountedRef]);

  // One card per live product, so amendment history no longer splits a product across cards.
  const productSubscriptions = useMemo(
    () => listedTargets(billingTargets, userDoc.contractView?.contractId),
    [billingTargets, userDoc],
  );

  const activationCandidates = useMemo(() => (
    oneTargetPerProduct(billingTargets.filter((target) => !!target.contractDoc && canActivateOnContract(target)))
  ), [billingTargets]);

  const onActivateUser = useCallback(async () => {
    const target = activationTarget;
    setActivationTarget(null);
    try {
      await userDoc.activateUser(loggedInUserId, target.contractDoc);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          userId,
          contractId: target?.contractId,
        },
      });
      // Detail stays in the Sentry capture above; the toast keeps it human.
      showToast(texts.activationFailed, { error: true });
    }
  }, [
    activationTarget,
    userDoc,
    loggedInUserId,
    userId,
    showToast,
  ]);

  // Landing on a live product would reset its service start date.
  const requestActivation = useCallback(
    () => requestContractChoice(activationCandidates, setActivationTarget, texts.activateUser),
    [activationCandidates, requestContractChoice],
  );

  if (!isReady) {
    return (
      <StyledLoadingPage />
    );
  }

  return (
    <>
      <Divider flexItem />
      <StyledAccordion
        key={texts.subscription}
        title={<StyledTitle>{texts.subscription}</StyledTitle>}
        expanded={expanded === accordionId.SUBSCRIPTION_ACCORDION || !isQAP}
        onChange={handleExpand(accordionId.SUBSCRIPTION_ACCORDION)}
        expandIcon={isQAP && <StyledArrowIcon />}
        disableGutters
      >
        <InfoContainer>
          {productSubscriptions.length > 0 ? productSubscriptions.map((target) => (
            <ProductSubscriptionRow
              key={target.contractId}
              target={target}
              planDoc={planDocsById[target.contractEntry.planId]}
              isOnlyProduct={productSubscriptions.length === 1}
            />
          )) : (
            <StyledSubTitle>
              {texts.noSubscription}
            </StyledSubTitle>
          )}
          {isOnCallUser && activationCandidates.length > 0 && (
            <StyledOnCallActions
              actions={[
                {
                  onClick: requestActivation,
                  disabled: false,
                  label: texts.activateUser,
                },
              ]}
            />
          )}
        </InfoContainer>
      </StyledAccordion>
      {!!pendingCandidates && (
        <ContractChooserModal
          title={texts.activateUser}
          description={texts.activateChooserDescription}
          candidates={pendingCandidates}
          onConfirm={confirmContractChoice}
          onCancel={dismissContractChoice}
        />
      )}
      <DialogRoundedModal
        open={!!activationTarget}
        title={texts.activateUser}
        onClose={() => setActivationTarget(null)}
        actionButtons={(
          <>
            <SaveButton onClick={onActivateUser}>
              {texts.buttons.activate}
            </SaveButton>
            <CancelButton onClick={() => setActivationTarget(null)}>
              {texts.buttons.cancel}
            </CancelButton>
          </>
        )}
      >
        <ActivateUserModalContent>
          {ReactHtmlParser(texts.activateUserDescription)}
        </ActivateUserModalContent>
      </DialogRoundedModal>
    </>
  );
};

const ProductSubscriptionRow = observer(({ target, planDoc, isOnlyProduct }) => {
  const { clientView, contractDoc } = target;
  const subscriptionData = target.subscriptionDoc?.subscriptionData;

  const clientStatus = useMemo(() => computed(() => getUserStatusWithDate(clientView)), [clientView]).get();

  const cancelText = useMemo(() => {
    if (!subscriptionData) {
      return EMPTY_CELL;
    }
    const cancelDate = moment.unix(subscriptionData.cancel_at);
    if (cancelDate.isAfter(moment())) {
      return cancelDate.format(DateFormat.MONTH_NAME_DATE_FORMAT);
    }
    if (subscriptionData.status === StripeSubscriptionState.CANCELED) {
      return `(${cancelDate.format(DateFormat.MONTH_NAME_DATE_FORMAT)})`;
    }
    return EMPTY_CELL;
  }, [subscriptionData]);

  const priceText = getUserSubscriptionPrice(contractDoc, planDoc) || EMPTY_CELL;

  const nextBillingText = useMemo(() => {
    if (subscriptionData && isLiveTarget(target)) {
      return moment.unix(getCurrentPeriodEnd(subscriptionData)).format(DateFormat.MONTH_NAME_DATE_FORMAT);
    }
    return EMPTY_CELL;
  }, [target, subscriptionData]);

  return (
    <Accordion
      title={productLabel(target)}
      defaultExpanded={isOnlyProduct}
    >
      <InfoWrapper>
        <InfoItem
          label={texts.clientStatus}
          contentColor={
            (clientStatus === texts.active || clientStatus?.includes(texts.amendingTerms))
              ? colors.shades.success6 : colors.shades.danger3
          }
        >
          {clientStatus || EMPTY_CELL}
        </InfoItem>
        <InfoItem label={texts.clientSince}>
          {subscriptionData
            ? moment.unix(subscriptionData.created).format(DateFormat.MONTH_NAME_DATE_FORMAT)
            : EMPTY_CELL}
        </InfoItem>
        <InfoItem label={texts.contractExpires}>
          {cancelText || EMPTY_CELL}
        </InfoItem>
        <InfoItem label={texts.package}>
          {planDoc?.planTitle || planDoc?.planCode || EMPTY_CELL}
        </InfoItem>
        <InfoItem label={texts.price}>
          {priceText || EMPTY_CELL}
        </InfoItem>
        <InfoItem label={texts.billingStatus}>
          {clientView.subscriptionStatus || EMPTY_CELL}
        </InfoItem>
        <InfoItem label={texts.nextBilling}>
          {nextBillingText || EMPTY_CELL}
        </InfoItem>
      </InfoWrapper>
    </Accordion>
  );
});

ProductSubscriptionRow.propTypes = {
  target: PropTypes.object.isRequired,
  planDoc: PropTypes.object,
  isOnlyProduct: PropTypes.bool,
};

ProductSubscriptionRow.defaultProps = {
  planDoc: null,
  isOnlyProduct: false,
};

SubscriptionDetailsSection.propTypes = {
  userDoc: PropTypes.object.isRequired,
  billingTargets: PropTypes.arrayOf(PropTypes.object),
  handleExpand: PropTypes.func.isRequired,
  expanded: PropTypes.string.isRequired,
  isQAP: PropTypes.bool.isRequired,
};

SubscriptionDetailsSection.defaultProps = {
  billingTargets: [],
};

export default compose(
  observer,
)(SubscriptionDetailsSection);
